import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      navigate("/portal/dashboard");
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId) => {
    const token = localStorage.getItem("client_token");
    if (!token) {
      navigate("/portal");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/stripe/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
      } else if (attempts < 5) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus(sessionId);
        }, 2000);
      } else {
        setStatus("failed");
      }
    } catch (error) {
      console.error("Status check error:", error);
      if (attempts < 5) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus(sessionId);
        }, 2000);
      } else {
        setStatus("failed");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="inline-block mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/n0vcoewk_fulllogo.png" 
            alt="Astute Builders" 
            className="h-20 mx-auto"
          />
        </Link>

        {status === "checking" && (
          <div className="bg-[#121212] border border-[#2A2A2A] p-12">
            <Loader2 size={64} className="text-[#7ED4C6] animate-spin mx-auto mb-6" />
            <h1 className="font-['Oswald'] text-2xl text-white mb-2">
              Processing Payment
            </h1>
            <p className="text-[#A1A1AA]">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-[#121212] border border-[#2A2A2A] p-12">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-green-400" />
            </div>
            <h1 className="font-['Oswald'] text-3xl text-white mb-2">
              Payment Successful!
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              Thank you for your payment. A receipt has been sent to your email.
            </p>
            <Link
              to="/portal/dashboard"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="back-to-dashboard"
            >
              Back to Dashboard
            </Link>
          </div>
        )}

        {status === "failed" && (
          <div className="bg-[#121212] border border-[#2A2A2A] p-12">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={48} className="text-red-400" />
            </div>
            <h1 className="font-['Oswald'] text-3xl text-white mb-2">
              Payment Issue
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              We couldn't confirm your payment. Please check your dashboard or contact support.
            </p>
            <Link
              to="/portal/dashboard"
              className="btn-primary inline-flex items-center gap-2"
            >
              Back to Dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
